import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Grid,
  Typography,
  CircularProgress,
  Chip,
  Button,
  Tooltip,
  TextField,
  Select,
  FormControl,
  InputLabel,
  MenuItem
} from "@mui/material";
import { useState, useMemo, useEffect, useCallback } from "react";
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import ImageCard from "../Modals/ImageCard";
import UploadModal from "../Modals/UploadModal";
import { AddPhotoAlternate } from "@mui/icons-material";
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { getReadonlyFlag } from "../../utils/getRoleToken";


const GaleriaImagensProduto = ({ open, onClose, produtoNome = "", produtoId = null }) => {

  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [modalUploadOpen, setModalUploadOpen] = useState(false);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchTermTemp, setSearchTermTemp] = useState("");
  const [startDateTemp, setStartDateTemp] = useState("");
  const [endDateTemp, setEndDateTemp] = useState("");
  const [ordenacao, setOrdenacao] = useState("data"); // 'data' ou 'nome'
  const [ordenacaoTemp, setOrdenacaoTemp] = useState("data");
  const readonly = getReadonlyFlag();

  const [todasImagens, setTodasImagens] = useState([]);

  const [imagemParaExcluir, setImagemParaExcluir] = useState(null);
  const [dialogExcluirOpen, setDialogExcluirOpen] = useState(false);
  const [excluindoImagem, setExcluindoImagem] = useState(false);
  const [imagensRecemEnviadas, setImagensRecemEnviadas] = useState(new Set());
  const [justificativaExclusao, setJustificativaExclusao] = useState("");

  const baseURL = process.env.REACT_APP_API_BASE_URL;

  // ADICIONE isto perto do topo do componente
  const fetchImagens = useCallback(async () => {
    if (!produtoId || !open) {
      setTodasImagens([]);
      return;
    }

    try {
      setIsLoading(true);

      const token = localStorage.getItem('jwtToken');
      if (!token) {
        console.error("Token não encontrado no localStorage.");
        return;
      }

      const decodedToken = jwtDecode(token);
      const idClienteAipim = decodedToken.id_cliente;

      const params = {
        idClienteAipim: idClienteAipim,
        idEmpreendimento: produtoId
      };

      const response = await axios.get(`${baseURL}/produto/sdr/imagens`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      const imagensFormatadas = response.data.map(img => ({
        id: img.id,
        url: `https://drive.google.com/uc?export=view&id=${img.id.trim()}`,
        nome: img.nome,
        descricao: img.descricao,
        dataUpload: img.criadoEm.split('T')[0]
      }));

      setTodasImagens(imagensFormatadas);
    } catch (error) {
      console.error("Erro ao buscar imagens:", error);
      if (error.response?.status === 401 ||
        error.response?.data?.message?.toLowerCase().includes("token")) {
        console.error("Token expirado ou inválido");
      }
    } finally {
      setIsLoading(false);
    }
  }, [baseURL, open, produtoId]);

  const fetchImagensRecemEnviadas = useCallback(async () => {
    if (imagensRecemEnviadas.size === 0) return;

    try {
      const token = localStorage.getItem('jwtToken');
      if (!token) return;

      const decodedToken = jwtDecode(token);
      const idClienteAipim = decodedToken.id_cliente;

      const params = {
        idClienteAipim: idClienteAipim,
        idEmpreendimento: produtoId
      };

      const response = await axios.get(`${baseURL}/produto/sdr/imagens`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      const imagensFormatadas = response.data.map(img => ({
        id: img.id,
        url: `https://drive.google.com/uc?export=view&id=${img.id.trim()}`,
        nome: img.nome,
        descricao: img.descricao,
        dataUpload: img.criadoEm.split('T')[0]
      }));

      // Atualiza SOMENTE as imagens recém enviadas
      setTodasImagens(prev => {
        return prev.map(imgExistente => {
          if (imagensRecemEnviadas.has(imgExistente.id)) {
            const imgAtualizada = imagensFormatadas.find(img => img.id === imgExistente.id);
            if (imgAtualizada) {
              return { ...imgAtualizada, __justUploaded: true };
            }
          }
          return imgExistente;
        });
      });

      // Limpa a lista de recém enviadas
      setImagensRecemEnviadas(new Set());
    } catch (error) {
      console.error("Erro ao buscar imagens recém enviadas:", error);
    }
  }, [baseURL, imagensRecemEnviadas, produtoId]);

  // Buscar imagens do backend
  useEffect(() => {
    fetchImagens();
  }, [fetchImagens]);

  // Busca imagens recém enviadas a cada 3 segundos
  useEffect(() => {
    if (imagensRecemEnviadas.size === 0) return;

    const interval = setInterval(() => {
      fetchImagensRecemEnviadas();
    }, 3000);

    return () => clearInterval(interval);
  }, [fetchImagensRecemEnviadas, imagensRecemEnviadas.size]);


  // Filtrar imagens baseado na pesquisa
  const imagensFiltradas = useMemo(() => {
    const filtradas = todasImagens.filter((imagem) => {
      const matchNome = imagem.nome
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      let matchData = true;
      if (startDate && endDate) {
        matchData = imagem.dataUpload >= startDate && imagem.dataUpload <= endDate;
      } else if (startDate) {
        matchData = imagem.dataUpload >= startDate;
      } else if (endDate) {
        matchData = imagem.dataUpload <= endDate;
      }

      return matchNome && matchData;
    });

    // Aplicar ordenação
    return filtradas.sort((a, b) => {
      if (ordenacao === "data") {
        return new Date(b.dataUpload) - new Date(a.dataUpload); // Mais recente primeiro
      } else {
        return a.nome.localeCompare(b.nome); // Ordem alfabética
      }
    });
  }, [searchTerm, startDate, endDate, todasImagens, ordenacao]);

  const handleApplyFilters = () => {
    setIsLoading(true);
    setTimeout(() => {
      setSearchTerm(searchTermTemp);
      setStartDate(startDateTemp);
      setEndDate(endDateTemp);
      setOrdenacao(ordenacaoTemp);
      setIsLoading(false);
    }, 500);
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setStartDate("");
    setEndDate("");
    setSearchTermTemp("");
    setStartDateTemp("");
    setEndDateTemp("");
    setOrdenacaoTemp("data");
    setOrdenacao("data");
  };

  const handleUploadSuccess = (novaImagem) => {
    const { tempId, id, nome, descricao, googleFileId, criadoEm } = novaImagem || {};

    const imagemFinal = {
      id: id,
      url: googleFileId ? `https://drive.google.com/uc?export=view&id=${String(googleFileId).trim()}` : null,
      nome: nome,
      descricao: descricao,
      dataUpload: (criadoEm || new Date().toISOString()).split('T')[0],
      __justUploaded: true, // ADICIONE ESTA LINHA
    };

    setImagensRecemEnviadas(prev => new Set([...prev, id]));

    setTodasImagens(prev => {
      const list = [...prev];
      const idx = list.findIndex(x => x.id === tempId && x.__placeholder);
      if (idx >= 0) {
        // Libera URL de preview
        if (list[idx].url && String(list[idx].url).startsWith("blob:")) {
          try { URL.revokeObjectURL(list[idx].url); } catch { }
        }
        list[idx] = imagemFinal;
        return list;
      }
      // Se não achar o placeholder, adiciona no topo
      return [imagemFinal, ...list];
    });

    // Remove a flag __justUploaded após 3 segundos
    setTimeout(() => {
      setTodasImagens(prev =>
        prev.map(img =>
          img.id === id ? { ...img, __justUploaded: false } : img
        )
      );
    }, 3000);
  };

  const handleUploadBegin = ({ tempId, nome, descricao, file }) => {
    const urlPreview = file ? URL.createObjectURL(file) : null;

    const placeholder = {
      id: tempId,
      url: urlPreview,           // preview local
      nome: nome || 'Enviando...',
      descricao: descricao || '',
      dataUpload: new Date().toISOString().split('T')[0],
      __placeholder: true        // flag interna
    };

    setTodasImagens(prev => [placeholder, ...prev]);
  };

  const handleDeleteImage = (fileId) => {
    const imagem = todasImagens.find(img => img.id === fileId);
    setImagemParaExcluir(imagem);
    setJustificativaExclusao("");
    setDialogExcluirOpen(true);
  };

  const confirmarExclusao = async () => {
    if (!imagemParaExcluir) return;

    setExcluindoImagem(true);

    try {
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        console.error("Token não encontrado no localStorage.");
        setExcluindoImagem(false);
        return;
      }

      const decodedToken = jwtDecode(token);
      const idClienteAipim = decodedToken.id_cliente;

      await axios.delete(`${baseURL}/produto/sdr/imagens/${imagemParaExcluir.id}`, {
        params: { idClienteAipim },
        headers: { Authorization: `Bearer ${token}` }
      });

      // **ENVIAR JUSTIFICATIVA DA EXCLUSÃO**
      try {
        const decoded = jwtDecode(token);
        const idUsuario = decoded?.id_usuario ?? decoded?.idUsuario ?? null;
        const nomeDoUsuario = decoded?.nome ?? decoded?.nomeUsuario ?? null;

        if (!idUsuario) {
          throw new Error("ID do usuário não encontrado no token JWT.");
        }

        if (!nomeDoUsuario) {
          throw new Error("Nome do usuário não encontrado no token JWT.");
        }
        
        const payloadJustificativa = {
          idClienteAipim: idClienteAipim,
          idUsuario: idUsuario,
          idProduto: 2,
          campo: "Galeria de Imagens",
          valorNovo: "",
          valorAnterior: "",
          justificativa: `${nomeDoUsuario} | [EXCLUIU] | Produto: ${produtoNome} | Imagem: ${imagemParaExcluir.nome} | Justificativa: ${justificativaExclusao}`,
        };

        console.log('=== PAYLOAD DA JUSTIFICATIVA (EXCLUSÃO) ===');
        console.log('Payload completo:', payloadJustificativa);

        await axios.post(
          `${baseURL}/produto/Justificativa`,
          payloadJustificativa,
          {
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            }
          }
        );

        console.log("Justificativa de exclusão enviada com sucesso");
      } catch (errJust) {
        console.error("Erro ao enviar justificativa:", errJust);
        // Não bloqueia a exclusão se a justificativa falhar
      }

      setTodasImagens((prev) => prev.filter((img) => img.id !== imagemParaExcluir.id));
      // Remove da lista de recém enviadas se estiver lá
      setImagensRecemEnviadas(prev => {
        const novo = new Set(prev);
        novo.delete(imagemParaExcluir.id);
        return novo;
      });
      setDialogExcluirOpen(false);
      setImagemParaExcluir(null);
    } catch (error) {
      console.error("Erro ao excluir imagem:", error?.response?.data || error.message);
    } finally {
      setExcluindoImagem(false);
    }
  };

  const handleEditImage = async (id, { nome, descricao }) => {
    try {
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        console.error("Token não encontrado no localStorage.");
        return;
      }

      const decodedToken = jwtDecode(token);
      const idClienteAipim = decodedToken.id_cliente;

      const fd = new FormData();
      fd.append('idClienteAipim', idClienteAipim);
      fd.append('novoNome', nome);
      fd.append('novaDescricao', descricao);

      await axios.put(
        `${baseURL}/produto/sdr/imagens/${encodeURIComponent(id)}`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          }
        }
      );

      // **Buscar dados anteriores da imagem**
      const imagemAnterior = todasImagens.find(img => img.id === id);
      if (!imagemAnterior) {
        throw new Error("Imagem não encontrada para comparação.");
      }

      // **Identificar o que mudou**
      const mudancas = [];
      let valorAnterior = "";
      let valorNovo = "";

      if (imagemAnterior.nome !== nome) {
        mudancas.push("Nome");
        valorAnterior += `"${imagemAnterior.nome}"`;
        valorNovo += `"${nome}"`;
      }

      if (imagemAnterior.descricao !== descricao) {
        if (mudancas.length > 0) {
          valorAnterior += " | ";
          valorNovo += " | ";
        }
        mudancas.push("Descrição");
        valorAnterior += `"${imagemAnterior.descricao}"`;
        valorNovo += `"${descricao}"`;
      }

      // Se nada mudou, não envia justificativa
      if (mudancas.length === 0) {
        console.log("Nenhuma alteração detectada, justificativa não será enviada.");
      } else {
        try {
          const decoded = jwtDecode(token);
          const idUsuario = decoded?.id_usuario ?? decoded?.idUsuario ?? null;
          const nomeDoUsuario = decoded?.nome ?? decoded?.nomeUsuario ?? null;

          if (!idUsuario) {
            throw new Error("ID do usuário não encontrado no token JWT.");
          }

          if (!nomeDoUsuario) {
            throw new Error("Nome do usuário não encontrado no token JWT.");
          }

          // --- NOVO TRECHO: formata a justificativa no padrão solicitado ---
          const mudouNome = mudancas.includes("Nome");
          const mudouDesc = mudancas.includes("Descrição");

          let oQueAlterou = "";
          if (mudouNome && mudouDesc) {
            oQueAlterou = "Nome e Descrição da Imagem";
          } else if (mudouNome) {
            oQueAlterou = "Nome da Imagem";
          } else if (mudouDesc) {
            oQueAlterou = "Descrição da Imagem";
          }

          // Se o nome mudou, use o nome novo na mensagem; caso contrário, o anterior
          const nomeImagemParaLog = mudouNome ? (nome ?? imagemAnterior.nome) : imagemAnterior.nome;

          // ATENÇÃO: [ALTEROU] deve ficar literal exatamente assim
          const justificativaTexto =
            `${nomeDoUsuario} | [ALTEROU] | Produto: ${produtoNome} | Imagem: ${nomeImagemParaLog} | ${oQueAlterou}`;

          // Monta o payload mantendo os campos existentes
          const payloadJustificativa = {
            idClienteAipim: idClienteAipim,
            idUsuario: idUsuario,
            idProduto: 2,
            campo: "Galeria de Imagens",
            valorNovo: valorNovo,
            valorAnterior: valorAnterior,
            justificativa: justificativaTexto
          };

          console.log('=== PAYLOAD DA JUSTIFICATIVA (EDIÇÃO) ===');
          console.log('Payload completo:', payloadJustificativa);

          const justificativaResponse = await axios.post(
            `${baseURL}/produto/Justificativa`,
            payloadJustificativa,
            {
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
              }
            }
          );

          if (justificativaResponse) {
            console.log("Justificativa de edição enviada com sucesso");
          }
        } catch (errJust) {
          console.error("Erro ao enviar justificativa:", errJust);
          throw new Error(`Falha ao registrar justificativa: ${errJust.message}`);
        }
      }

      // Atualiza no estado local
      setTodasImagens(prev =>
        prev.map(img => (img.id === id ? { ...img, nome, descricao } : img))
      );
    } catch (error) {
      console.error("Erro ao atualizar imagem:", error?.response?.data || error.message);
    }
  };

  const handleCloseGaleria = () => {
    setImagensRecemEnviadas(new Set());
    onClose();
  };

  const temMudancasFiltros = useMemo(() => {
    return (
      searchTermTemp !== searchTerm ||
      startDateTemp !== startDate ||
      endDateTemp !== endDate ||
      ordenacaoTemp !== ordenacao
    );
  }, [searchTermTemp, searchTerm, startDateTemp, startDate, endDateTemp, endDate, ordenacaoTemp, ordenacao]);

  return (
    <Dialog
      open={open}
      onClose={handleCloseGaleria}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '80vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          pb: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', gap: 2 }}>
              {produtoNome && (
                <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 0.3 }}>
                  {produtoNome}
                </Typography>
              )}
              <Typography variant="caption" sx={{ color: '#666', lineHeight: 0.3 }}>
                Galeria de Imagens
              </Typography>
            </Box>
          </Box>
          {/* BOTÃƒO DE UPLOAD */}
          {!readonly && (
            <Tooltip title="Cadastrar novas imagens" arrow>
              <IconButton
                onClick={() => setModalUploadOpen(true)}
                sx={{
                  ml: 2,
                  color: '#1976d2',
                  width: 64,  // Tamanho fixo maior
                  height: 64, // Tamanho fixo maior
                  '&:hover': {
                    bgcolor: 'rgba(25, 118, 210, 0.04)'
                  }
                }}
              >
                <AddPhotoAlternate sx={{ fontSize: 40 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handleCloseGaleria} sx={{ color: '#666' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Contador de Resultados */}
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={`${imagensFiltradas.length} ${imagensFiltradas.length === 1 ? 'imagem encontrada' : 'imagens encontradas'}`}
            size="small"
            sx={{
              bgcolor: '#e3f2fd',
              color: '#1976d2',
              fontWeight: 500
            }}
          />
        </Box>

        {/* Barra de Filtros */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
            mb: 2,
          }}
        >
          {/* Select de Ordenação */}
          <FormControl size="small" sx={{ width: 165 }}>
            <InputLabel>Ordenar por</InputLabel>
            <Select
              value={ordenacaoTemp}
              onChange={(e) => setOrdenacaoTemp(e.target.value)}
              label="Ordenar por"
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 1.5,
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#667eea',
                },
              }}
            >
              <MenuItem value="data">Data</MenuItem>
              <MenuItem value="nome">Nome</MenuItem>
            </Select>
          </FormControl>

          {/* Campo de busca por nome */}
          <TextField
            label="Buscar por nome"
            value={searchTermTemp}
            onChange={(e) => setSearchTermTemp(e.target.value)}
            variant="outlined"
            size="small"
            placeholder="Digite o nome"
            InputLabelProps={{
              shrink: searchTermTemp ? true : false
            }}
            sx={{
              width: 165,
              bgcolor: 'rgba(255,255,255,0.95)',
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
                '&.Mui-focused fieldset': {
                  borderColor: '#667eea',
                },
              },
            }}
          />

          {/* Botões */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleApplyFilters}
              disabled={!temMudancasFiltros}  // ADICIONAR ESTA LINHA
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '0.875rem',
                px: 3,
                py: 1,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.3)',
                  transform: 'translateY(-1px)',
                },
                '&:disabled': {  // ADICIONAR ESTE BLOCO
                  background: 'linear-gradient(135deg, #ccc 0%, #aaa 100%)',
                  color: 'white',
                  cursor: 'not-allowed',
                },
              }}
            >
              Aplicar Filtros
            </Button>

            <Button
              variant="contained"
              onClick={handleClearFilters}
              sx={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.25) 100%)',
                color: '#1a1a1a',
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '0.875rem',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                px: 3,
                py: 1,
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.35) 100%)',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.6)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Limpar Filtros
            </Button>
          </Box>
        </Box>


        {/* Loading */}
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 300,
              gap: 2
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ color: '#666' }}>
              Carregando imagens...
            </Typography>
          </Box>
        ) : (
          <>
            {/* Grid de Imagens */}
            {imagensFiltradas.length > 0 ? (
              <Box>
                <Grid container spacing={3}>
                  {imagensFiltradas.map((imagem) => (
                    <ImageCard
                      key={imagem.id}
                      imagem={imagem}
                      onDelete={handleDeleteImage}
                      onEdit={handleEditImage}
                      readonly={readonly}
                    />
                  ))}
                </Grid>
              </Box>

            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  minHeight: 300,
                  gap: 2
                }}
              >
                <ImageIcon sx={{ fontSize: 64, color: '#ccc' }} />
                <Typography variant="h6" sx={{ color: '#999' }}>
                  Nenhuma imagem encontrada
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      {/* MODAL DE UPLOAD */}
      <UploadModal
        open={modalUploadOpen}
        onClose={() => setModalUploadOpen(false)}
        onUploadSuccess={handleUploadSuccess}
        onUploadBegin={handleUploadBegin}
        produtoId={produtoId}
        produtoNome={produtoNome}
        baseURL={baseURL}
      />

      {/* DIALOG DE CONFIRMAÇÃO DE EXCLUSÃO */}
      <Dialog
        open={dialogExcluirOpen}
        onClose={() => setDialogExcluirOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          bgcolor: '#fff3e0',
          color: '#e65100',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          ⚠️ Confirmar Exclusão
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
            Tem certeza que deseja excluir a imagem "{imagemParaExcluir?.nome}"?
          </Typography>
          <Box sx={{
            bgcolor: '#ffebee',
            p: 2,
            borderRadius: 1,
            border: '1px solid #ffcdd2'
          }}>
            <Typography variant="body2" sx={{ color: '#c62828', fontWeight: 500 }}>
              Esta ação fará com que seu agente pare de enviar esta foto para os seus leads.
            </Typography>
          </Box>
          <TextField
            label="Justificativa de Exclusão"
            placeholder="Descreva o motivo da exclusão desta imagem (ex: imagem desatualizada, foto incorreta, substituída por nova versão, etc.)"
            value={justificativaExclusao}
            onChange={(e) => setJustificativaExclusao(e.target.value)}
            fullWidth
            multiline
            rows={3}
            required
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            onClick={() => setDialogExcluirOpen(false)}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmarExclusao}
            variant="contained"
            color="error"
            disabled={excluindoImagem || !justificativaExclusao.trim()}
            sx={{
              textTransform: 'none',
              minWidth: 140
            }}
          >
            {excluindoImagem ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'white' }} />
                <span>Excluindo...</span>
              </Box>
            ) : (
              'Excluir Imagem'
            )}
          </Button>
        </Box>
      </Dialog>
    </Dialog>
  );
};

export default GaleriaImagensProduto;

